import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { EmptyArray, sum } from 'lib/array/arrayUtils'
import KeyValueSection from '../SearchInputTriggerKeyValue'
import BodyText from 'LuxKit/Typography/BodyText'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import AIGlowText from '../AIGlowText'
import Caption from 'LuxKit/Typography/Caption'

function Content({ showAIGlow }: { showAIGlow?: boolean }) {
  const {
    occupancies = EmptyArray,
    disableSaleUnit,
  } = useContext(GlobalSearchStateContext)
  const containsChildren = occupancies.some(room => room.childrenAge?.length)
  const guestCount = useMemo(() => sum(occupancies, room => room.adults + (room.childrenAge?.length ?? 0)), [occupancies])
  const intl = useIntl()

  const guestsLabel = intl.formatMessage({
    id: '0PyVbFYRn2',
    defaultMessage: 'Guests',
    description: 'Label for guests section in search travellers input',
  })

  const selectAdultsChildrenLabel = intl.formatMessage({
    id: 'Y3QL1w1uSm',
    defaultMessage: 'Select adults/children',
    description: 'Placeholder text when no guests are selected in search travellers input',
  })

  let travellerText: string
  if (!occupancies.length) {
    travellerText = selectAdultsChildrenLabel
  } else if (containsChildren && disableSaleUnit) {
    travellerText = intl.formatMessage({
      id: 'vwTl8YQ9TN',
      defaultMessage: '{guestCount, plural, one {# guest} other {# guests}}',
      description: 'Guest count when occupancies include children and sale unit is disabled in search travellers input',
    }, { guestCount })
  } else if (containsChildren) {
    // eslint-disable-next-line formatjs/no-multiple-plurals
    travellerText = intl.formatMessage({
      id: 'BWayv/+r+3',
      defaultMessage: '{guestCount, plural, one {# guest} other {# guests}} ({roomCount, plural, one {# room} other {# rooms}})',
      description: 'Guest count with room count when occupancies include children in search travellers input',
    }, { guestCount, roomCount: occupancies.length })
  } else if (disableSaleUnit) {
    travellerText = intl.formatMessage({
      id: '4wpbgM3U6V',
      defaultMessage: '{guestCount, plural, one {# adult} other {# adults}}',
      description: 'Adult count when sale unit is disabled in search travellers input',
    }, { guestCount })
  } else {
    // eslint-disable-next-line formatjs/no-multiple-plurals
    travellerText = intl.formatMessage({
      id: 'U99et4pR2z',
      defaultMessage: '{guestCount, plural, one {# adult} other {# adults}} ({roomCount, plural, one {# room} other {# rooms}})',
      description: 'Adult count with room count in search travellers input',
    }, { guestCount, roomCount: occupancies.length })
  }

  return (
    <KeyValueSection>
      <Caption colour="neutral-one" variant="large" weight="bold">{guestsLabel}</Caption>
      <AIGlowText displayGlow={showAIGlow}>
        <BodyText variant="medium" colour="neutral-two">
          {travellerText}
        </BodyText>
      </AIGlowText>
    </KeyValueSection>
  )
}

export default Content
